import { createSelector } from 'reselect';

import { jobSelector, jobForJobApplicantIdSelector } from './job-selectors';

import { RequestState, StateDictionary } from './utils/state-manager';

type OrgProcessPerformance = {
  [id: number]: {
    reportingData: ReportingData;
    applicantSummary: ApplicantSummary;
    metaData: ApplicantMetaData;
  };
};
type OrgDictionary = { [id: number]: Org };
type OrgMergeEmployeeMetaPostDictionary = { [orgId: number]: MergeMetaPostType };
export type OrgState = {
  orgsById: OrgDictionary;
  orgsStateById: StateDictionary;
  orgsStateByAccountId: StateDictionary;
  orgProcessPerformanceById: OrgProcessPerformance;
  orgProcessPerformanceByIdStatus: StateDictionary;
  orgMergeEmployeeMetaPostByOrgId: OrgMergeEmployeeMetaPostDictionary;
  orgMergeEmployeeMetaPostStateByOrgId: StateDictionary;
};
type State = { orgs: OrgState };

export const orgsSelector = (state: State) => Object.values(state.orgs.orgsById);
export const orgsByIdSelector = (state: State): OrgDictionary => state.orgs.orgsById;

export const orgSelector = createSelector(
  [orgsByIdSelector, (_state: State, orgId: number) => orgId],
  (orgsById: OrgDictionary, orgId: number) => (orgsById ? orgsById[orgId] : null),
);

export const orgsStateByIdSelector = (state: State) => state.orgs.orgsStateById;
export const orgStatusSelector = createSelector(
  [orgsStateByIdSelector, (_state: State, orgId: number) => orgId],
  (orgStateById: StateDictionary, orgId: number): RequestState => orgStateById[orgId],
);
export const orgsStateByAccountIdSelector = (state: State) => state.orgs.orgsStateByAccountId;
export const orgStatusSelectorByAccountId = createSelector(
  [orgsStateByAccountIdSelector, (_state: State, accountId: number) => accountId],
  (orgStateByAccountId: StateDictionary, accountId: number): RequestState =>
    orgStateByAccountId[accountId],
);

export const orgsForAccountId = createSelector(
  [orgsByIdSelector, (_state: State, accountId: number) => accountId],
  (orgsById: OrgDictionary, accountId: number) =>
    orgsById
      ? Object.values(orgsById).filter((org) => org.accountId === accountId && org.isActive)
      : null,
);

const getOrgFromJob = (orgsById: OrgDictionary | null, job: Job | null) =>
  orgsById && job ? orgsById[job.orgId] : null;

export const orgForJobIdSelector = createSelector([orgsByIdSelector, jobSelector], getOrgFromJob);
export const orgForJobApplicantIdSelector = createSelector(
  [orgsByIdSelector, jobForJobApplicantIdSelector],
  getOrgFromJob,
);

export const orgProcessPerformanceByIdStatusSelector = (state: State) =>
  state.orgs.orgProcessPerformanceByIdStatus;
export const orgProcessPerformanceStateForOrgIdSelector = createSelector(
  [orgProcessPerformanceByIdStatusSelector, (_state: State, orgId: number) => orgId],
  (orgProcessPerformanceByIdStatus: StateDictionary, orgId: number) =>
    orgProcessPerformanceByIdStatus[orgId],
);

export const orgProcessPerformanceByIdSelector = (state: State) =>
  state.orgs.orgProcessPerformanceById;
export const orgProcessPerformanceForOrgIdSelector = createSelector(
  [orgProcessPerformanceByIdSelector, (_state: State, orgId: number) => orgId],
  (orgProcessPerformanceById: OrgProcessPerformance, orgId: number) =>
    orgProcessPerformanceById[orgId],
);

export const orgMergeEmployeeMetaPostByOrgIdSelector = (state: State) =>
  state.orgs.orgMergeEmployeeMetaPostByOrgId;

export const orgMergeEmployeeMetaPostForOrgIdSelector = createSelector(
  [orgMergeEmployeeMetaPostByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (orgMergeEmployeeMetaPostById: OrgMergeEmployeeMetaPostDictionary, orgId: number) =>
    orgMergeEmployeeMetaPostById[orgId],
);

export const orgMergeEmployeeMetaPostStateByOrgIdSelector = (state: State) =>
  state.orgs.orgMergeEmployeeMetaPostStateByOrgId;

export const orgMergeEmployeeMetaPostStateForOrgIdSelector = createSelector(
  [orgMergeEmployeeMetaPostStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (orgMergeEmployeeMetaPostStateByOrgId: StateDictionary, orgId: number) =>
    orgMergeEmployeeMetaPostStateByOrgId[orgId],
);
